import { Component, EventEmitter, Input, Output } from '@angular/core';
import { TDurationFilterType } from '../filter/model/ui-filter.types';
import { DurationPickerComponent } from '../duration-picker/duration-picker.component';
import { AlertController, ModalController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import moment from 'moment';

@Component({
  selector: 'monorepo-date-filter',
  templateUrl: './date-filter.component.html',
  styleUrls: ['./date-filter.component.css'],
})
export class DateFilterComponent {
  dateDs = '';
  dateFilter: TDurationFilterType = {
    type: 'TODAY',
  };

  @Input() startLimit: number = 30;
  @Input() range: number = 7;

  @Output() onDateFilterChange = new EventEmitter<{
    startDate: number;
    endDate: number;
  }>();

  constructor(
    private modalController: ModalController,
    private translateService: TranslateService,
    private alrtCtrl: AlertController,
  ) {
    this.dateDs = this.updateDateLabelString(this.dateFilter);
  }

  async openDurationComponent() {
    const modal = await this.modalController.create({
      component: DurationPickerComponent,
      componentProps: {
        selectedDateFilter: this.dateFilter,
      },
      cssClass: 'duration-picker-modal',
    });
    await modal.present();
    modal.onDidDismiss().then(async (res) => {
      if (res.data) {
        if (this.isValidDateAndTime(res.data, this.startLimit, this.range)) {
          this.dateFilter = res.data;
          this.dateDs = this.updateDateLabelString(this.dateFilter);
          this.onDateFilterChange.emit({
            startDate: this.getStartAndEndTime(this.dateFilter).start,
            endDate: this.getStartAndEndTime(this.dateFilter).end,
          });
        }
      }
    });
  }

  isValidDateAndTime(dateFilter: TDurationFilterType, startLimit: number, range: number): boolean {
    if (dateFilter.type !== 'CUSTOM') return true;
    const startAndEndTime = this.getStartAndEndTime(dateFilter);
    if (startAndEndTime.start > startAndEndTime.end) {
      this.handleError(this.translateService.instant('date_filter.dateError'));
      return false;
    } else if (
      new Date().setHours(0, 0, 0, 0) - new Date(startAndEndTime.start).setHours(0, 0, 0, 0) >
      startLimit * 86400000
    ) {
      this.handleError(this.translateService.instant('date_filter.START_OUT_OF_RANGE', { days: startLimit }));
      return false;
    } else if (
      new Date(startAndEndTime.end).setHours(0, 0, 0, 0) - new Date(startAndEndTime.start).setHours(0, 0, 0, 0) >
      range * 86400000
    ) {
      this.handleError(this.translateService.instant('date_filter.RANGE_OUT_OF_LENGTH', { days: range }));
      return false;
    } else return true;
  }

  updateDateLabelString(dateFilter: TDurationFilterType) {
    const startAndEndTime = this.getStartAndEndTime(dateFilter);
    if (startAndEndTime.start === startAndEndTime.end) {
      return moment(startAndEndTime.start).format('DD-MM-YYYY');
    } else {
      const startDate = moment(startAndEndTime.start).format('DD-MM-YYYY');
      const endDate = moment(startAndEndTime.end).format('DD-MM-YYYY');
      return startDate + ' - ' + endDate;
    }
  }

  getStartAndEndTime(dateFilter: TDurationFilterType) {
    const currentTime = moment().valueOf();
    switch (dateFilter.type) {
      case 'TODAY': {
        return {
          start: currentTime,
          end: currentTime,
        };
      }
      case 'YESTERDAY': {
        return {
          start: moment(currentTime).subtract(1, 'days').valueOf(),
          end: moment(currentTime).subtract(1, 'days').valueOf(),
        };
      }
      case 'WEEKLY': {
        return {
          start: moment(currentTime).subtract(6, 'days').valueOf(),
          end: currentTime,
        };
      }
      case 'MONTHLY': {
        return {
          start: moment(currentTime).subtract(30, 'days').valueOf(),
          end: currentTime,
        };
      }
      case 'CUSTOM': {
        return {
          start: dateFilter.start as number,
          end: dateFilter.end as number,
        };
      }
    }
  }

  async handleError(error: string) {
    const alert = await this.alrtCtrl.create({
      header: this.translateService.instant('emb_common.error'),
      subHeader: error,
      buttons: [
        {
          text: this.translateService.instant('common_ui.ok'),
        },
      ],
    });
    alert.present();
  }
}
